"use client";

import React, { useState, useEffect, useMemo } from "react";
import { getSanityClient } from "@/sanity/lib/client";
import Image from "next/image";
import { useCartContext } from "@/context/CartContext";
import Link from "next/link";
import { useSearchParams } from "next/navigation";

interface Product {
  _id: string;
  title: string;
  price: number;
  description: string;
  discountPercentage: number;
  imageUrl: string;
  productImage: { asset: { _ref: string } };
  tags: string[];
  categoryName: string;
  categoryParentId?: string;
  categoryParentName?: string;
  slug: string;
  _createdAt: string;
  inventory?: number;
}

interface CategoryInfo {
  _id: string;
  name: string;
  parentId?: string;
  parentName?: string;
}

type SortOption = "newest" | "price-asc" | "price-desc" | "best-selling";

const ProductCards: React.FC = () => {
  const { addToCart } = useCartContext();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Parse category filter from query parameters
  const searchParams = useSearchParams();
  const categoryParam = searchParams.get("category");

  // Core Filter states
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
  const [maxPrice, setMaxPrice] = useState(10000);
  const [showDiscountedOnly, setShowDiscountedOnly] = useState(false);
  const [selectedTag, setSelectedTag] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  // Sort state
  const [sortBy, setSortBy] = useState<SortOption>("newest");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 50;

  // Mobile drawer state
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [addedItems, setAddedItems] = useState<Set<string>>(new Set());
  const [addingItems, setAddingItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (categoryParam) {
      setSelectedCategory(categoryParam);
    } else {
      setSelectedCategory("All");
    }
  }, [categoryParam]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const query = `*[_type == "product"] | order(_createdAt desc) {
          _id, title, price, description, discountPercentage,
          "imageUrl": productImage.asset->url,
          tags,
          "categoryName": category->name,
          "categoryParentId": category->parent->_id,
          "categoryParentName": category->parent->name,
          "slug": slug.current,
          inventory,
          _createdAt
        }`;

        const categoriesQuery = `*[_type == "category"] | order(name asc) {
          _id, name, "parentId": parent->_id, "parentName": parent->name
        }`;
        
        const client = getSanityClient();
        if (!client) {
          setError(true);
          setLoading(false);
          return;
        }
        
        const data: Product[] = await client.fetch(query);
        const categoriesData: CategoryInfo[] = await client.fetch(categoriesQuery);

        setCategories(categoriesData);

        const validData = data as Product[];

        const highest = Math.max(...validData.map((p) => p.price), 0);
        const ceilPrice = Math.ceil(highest / 100) * 100 || 1000;
        setMaxPrice(ceilPrice);
        setPriceRange([0, ceilPrice]);

        setProducts(validData);
      } catch (error) {
        console.error("Error fetching products:", error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, priceRange, showDiscountedOnly, selectedTag, sortBy, searchQuery]);

  // Compute category counts
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    products.forEach((p) => {
      if (p.categoryName) {
        counts[p.categoryName] = (counts[p.categoryName] || 0) + 1;
      }
      if (p.categoryParentName) {
        counts[p.categoryParentName] = (counts[p.categoryParentName] || 0) + 1;
      }
    });
    return counts;
  }, [products]);

  // Compute dynamic tags cloud
  const allTags = useMemo(() => {
    const tagsSet = new Set<string>();
    products.forEach((p) => {
      if (p.tags && Array.isArray(p.tags)) {
        p.tags.forEach((tag) => {
          if (tag && tag.trim()) tagsSet.add(tag.trim());
        });
      }
    });
    return Array.from(tagsSet).sort();
  }, [products]);

  // Filter & Sort Pipeline
  const processedProducts = useMemo(() => {
    let result = [...products];

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          (p.categoryName && p.categoryName.toLowerCase().includes(q))
      );
    }

    // Category filter
    if (selectedCategory !== "All") {
      result = result.filter((p) => 
        p.categoryName === selectedCategory || p.categoryParentName === selectedCategory
      );
    }

    // Tag filter
    if (selectedTag !== "All") {
      result = result.filter((p) => p.tags && p.tags.includes(selectedTag));
    }

    // Price range filter
    result = result.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Discount filter
    if (showDiscountedOnly) {
      result = result.filter((p) => p.discountPercentage && p.discountPercentage > 0);
    }

    // Sorting
    switch (sortBy) {
      case "newest":
        result.sort((a, b) => new Date(b._createdAt).getTime() - new Date(a._createdAt).getTime());
        break;
      case "price-asc":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-desc":
        result.sort((a, b) => b.price - a.price);
        break;
      case "best-selling":
        result.sort((a, b) => (b.discountPercentage || 0) - (a.discountPercentage || 0));
        break;
    }

    return result;
  }, [products, selectedCategory, priceRange, showDiscountedOnly, selectedTag, sortBy, searchQuery]);

  // Pagination
  const totalPages = Math.ceil(processedProducts.length / productsPerPage);
  const currentProducts = processedProducts.slice(
    (currentPage - 1) * productsPerPage,
    currentPage * productsPerPage
  );

  const handleAddToCart = (product: Product) => {
    if (addingItems.has(product._id)) return;
    setAddingItems((prev) => new Set(prev).add(product._id));
    addToCart({ ...product, quantity: 1 });
    setTimeout(() => {
      setAddingItems((prev) => { const s = new Set(prev); s.delete(product._id); return s; });
      setAddedItems((prev) => new Set(prev).add(product._id));
      setTimeout(() => {
        setAddedItems((prev) => { const s = new Set(prev); s.delete(product._id); return s; });
      }, 2000);
    }, 400);
  };

  const clearFilters = () => {
    setSelectedCategory("All");
    setPriceRange([0, maxPrice]);
    setShowDiscountedOnly(false);
    setSelectedTag("All");
    setSortBy("newest");
    setSearchQuery("");
  };

  const hasActiveFilters = selectedCategory !== "All" || priceRange[0] > 0 || priceRange[1] < maxPrice || showDiscountedOnly || selectedTag !== "All" || searchQuery !== "";

  if (loading) {
    return (
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-8">
          <div className="w-1/4 hidden lg:block bg-gray-50 h-[500px] rounded-2xl animate-pulse" />
          <div className="flex-1 space-y-6">
            <div className="bg-gray-100 rounded-xl h-14 w-full animate-pulse" />
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={idx} className="bg-white border border-gray-100 rounded-2xl p-4 flex flex-col shadow-sm">
                  <div className="aspect-square w-full bg-gray-200 rounded-xl animate-pulse" />
                  <div className="h-4 bg-gray-200 w-3/4 rounded mt-4 animate-pulse" />
                  <div className="h-3 bg-gray-100 w-1/2 rounded mt-2 animate-pulse" />
                  <div className="h-6 bg-gray-200 w-1/3 rounded mt-4 animate-pulse" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Products unavailable</h2>
        <p className="text-gray-600">We are currently experiencing issues connecting to our catalog. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-2">
      <div className="flex flex-col lg:flex-row gap-8 items-start">
        
        {/* ─── Left Sidebar (Desktop Filters) ─── */}
        <aside className="w-full lg:w-[280px] flex-shrink-0 hidden lg:block sticky top-28 space-y-5">
          {/* Quick Clear Filter Option */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="w-full py-3 px-4 bg-red-50 hover:bg-red-100 border border-red-200 hover:border-red-300 text-red-700 rounded-xl text-xs font-bold transition-all duration-200 flex items-center justify-center gap-2 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Clear All Active Filters
            </button>
          )}

          {/* Categories Card */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
            <h2 className="text-xs font-extrabold text-gray-900 uppercase tracking-widest mb-4 border-b border-gray-50 pb-2">
              Categories
            </h2>
            <div className="space-y-1">
              <button
                onClick={() => setSelectedCategory("All")}
                className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                  selectedCategory === "All"
                    ? "bg-[#F9F1E7] text-[#B88E2F] font-bold"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                <span>All Categories</span>
                <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                  {products.length}
                </span>
              </button>

              {categories.filter(c => !c.parentId).map((parent) => {
                const parentCount = categoryCounts[parent.name] || 0;
                const subCats = categories.filter(sub => sub.parentId === parent._id);
                return (
                  <div key={parent._id} className="space-y-0.5">
                    <button
                      onClick={() => setSelectedCategory(parent.name)}
                      className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                        selectedCategory === parent.name
                          ? "bg-[#F9F1E7] text-[#B88E2F] font-bold"
                          : "text-gray-700 hover:bg-gray-50 hover:text-gray-900"
                      }`}
                    >
                      <span className="truncate">{parent.name}</span>
                      {parentCount > 0 && (
                        <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                          {parentCount}
                        </span>
                      )}
                    </button>
                    {/* Render Subcategories */}
                    {subCats.map((sub) => {
                      const subCount = categoryCounts[sub.name] || 0;
                      return (
                        <button
                          key={sub._id}
                          onClick={() => setSelectedCategory(sub.name)}
                          className={`w-full flex items-center justify-between text-left pl-7 pr-3 py-1.5 rounded-xl text-[13px] font-medium transition-all duration-200 ${
                            selectedCategory === sub.name
                              ? "bg-[#F9F1E7]/60 text-[#B88E2F] font-semibold"
                              : "text-gray-500 hover:bg-gray-50 hover:text-gray-800"
                          }`}
                        >
                          <span className="truncate">{sub.name}</span>
                          {subCount > 0 && (
                            <span className="text-[9px] bg-gray-100/80 text-gray-400 px-1.5 py-0.2 rounded-full">
                              {subCount}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

                {/* Promotions Filter Card */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
            <h2 className="text-xs font-extrabold text-gray-900 uppercase tracking-widest mb-3 border-b border-gray-50 pb-2">
              Promotions
            </h2>
            <div className="space-y-1.5">
              <label className="flex items-center gap-3 cursor-pointer py-1.5 px-2 hover:bg-gray-50 rounded-xl transition-colors">
                <input
                  type="checkbox"
                  checked={showDiscountedOnly}
                  onChange={(e) => setShowDiscountedOnly(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-300 text-[#B88E2F] focus:ring-[#B88E2F]"
                />
                <span className="text-sm font-semibold text-gray-750">Discounted / On Sale</span>
                <span className="text-[9px] text-red-650 font-bold bg-red-50 px-1.5 py-0.2 rounded">SALE</span>
              </label>
            </div>
          </div>

          {/* Price Range Filter Card */}
          <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
            <h2 className="text-xs font-extrabold text-gray-900 uppercase tracking-widest mb-4 border-b border-gray-50 pb-2">
              Price Range
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between text-xs text-gray-400 font-extrabold">
                <span>Min: Rs. 0</span>
                <span>Max: Rs. {maxPrice.toLocaleString()}</span>
              </div>
              
              <div className="space-y-2">
                <input
                  type="range"
                  min={0}
                  max={maxPrice}
                  step={50}
                  value={priceRange[1]}
                  onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                  className="w-full accent-[#B88E2F] h-1.5 bg-gray-100 rounded-lg appearance-none cursor-pointer"
                />
                <div className="text-sm font-bold text-gray-800">
                  Up to Rs. {priceRange[1].toLocaleString()}
                </div>
              </div>
            </div>
          </div>

          {/* Tags Cloud Filter Card */}
          {allTags.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm">
              <h2 className="text-xs font-extrabold text-gray-900 uppercase tracking-widest mb-3 border-b border-gray-50 pb-2">
                Filter by Tags
              </h2>
              <div className="flex flex-wrap gap-2 pt-1">
                <button
                  onClick={() => setSelectedTag("All")}
                  className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 border ${
                    selectedTag === "All"
                      ? "bg-[#B88E2F] border-[#B88E2F] text-white"
                      : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                  }`}
                >
                  All Tags
                </button>
                {allTags.map((tag) => (
                  <button
                    key={tag}
                    onClick={() => setSelectedTag(tag)}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all duration-200 border ${
                      selectedTag === tag
                        ? "bg-[#B88E2F] border-[#B88E2F] text-white shadow-sm"
                        : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                    }`}
                  >
                    #{tag}
                  </button>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* ─── Right Content Column (Toolbar & Grid) ─── */}
        <div className="flex-1 w-full space-y-6">
          
          {/* Swipeable Horizontal Categories for Mobile/Tablet */}
          <div className="lg:hidden w-full overflow-x-auto pb-2 scrollbar-hide flex gap-2 scroll-smooth">
            <button
              onClick={() => setSelectedCategory("All")}
              className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 ${
                selectedCategory === "All"
                  ? "bg-[#B88E2F] text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-[#F9F1E7]"
              }`}
            >
              All ({products.length})
            </button>
            {categories.map((cat) => {
              const count = categoryCounts[cat.name] || 0;
              if (count === 0) return null;
              return (
                <button
                  key={cat._id}
                  onClick={() => setSelectedCategory(cat.name)}
                  className={`px-4 py-2 rounded-full text-xs font-bold whitespace-nowrap transition-all duration-200 ${
                    selectedCategory === cat.name
                      ? "bg-[#B88E2F] text-white shadow-sm"
                      : "bg-gray-100 text-gray-600 hover:bg-[#F9F1E7]"
                  }`}
                >
                  {cat.name} ({count})
                </button>
              );
            })}
          </div>

          {/* Toolbar Control Bar */}
          <div className="bg-[#FAF7F2] rounded-2xl p-4 flex flex-col md:flex-row items-center justify-between gap-4 border border-gray-100 shadow-sm">
            {/* Search Input & Mobile Filter Toggle */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <div className="relative flex-1 md:flex-initial">
                <input
                  type="text"
                  placeholder="Search item..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full md:w-64 pl-10 pr-8 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#B88E2F] transition-all bg-white"
                />
                <svg className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" fill="none" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                {searchQuery && (
                  <button 
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs bg-gray-100 hover:bg-gray-200 w-5 h-5 flex items-center justify-center rounded-full"
                  >
                    ✕
                  </button>
                )}
              </div>

              {/* Mobile filter button trigger */}
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="lg:hidden p-2 bg-white rounded-xl border border-gray-200 text-gray-600 hover:text-black flex items-center gap-1.5 text-sm font-semibold"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
                {hasActiveFilters && <span className="w-1.5 h-1.5 rounded-full bg-red-500" />}
              </button>
            </div>

            {/* Results count & Sorting */}
            <div className="flex items-center justify-between md:justify-end gap-6 w-full md:w-auto">
              <span className="text-xs sm:text-sm text-gray-500 font-semibold">
                Found {processedProducts.length} items
              </span>

              <div className="flex items-center gap-2">
                <span className="text-xs sm:text-sm text-gray-600 font-bold whitespace-nowrap">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as SortOption)}
                  className="px-3 py-2 rounded-xl border border-gray-250 bg-white text-xs sm:text-sm font-bold text-gray-800 outline-none cursor-pointer focus:border-[#B88E2F]"
                >
                  <option value="newest">Newest Products</option>
                  <option value="price-asc">Price: Low → High</option>
                  <option value="price-desc">Price: High → Low</option>
                  <option value="best-selling">Deals / Discounts</option>
                </select>
              </div>
            </div>
          </div>

          {/* Active Filter Chips */}
          {hasActiveFilters && (
            <div className="flex flex-wrap items-center gap-2 bg-gray-55 p-3.5 rounded-2xl border border-gray-100">
              <span className="text-xs text-gray-400 font-extrabold uppercase tracking-widest mr-1">Active:</span>
              
              {selectedCategory !== "All" && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-700 shadow-sm">
                  Category: {selectedCategory}
                  <button onClick={() => setSelectedCategory("All")} className="text-gray-400 hover:text-red-500 font-bold">✕</button>
                </span>
              )}

              {selectedTag !== "All" && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-700 shadow-sm">
                  Tag: #{selectedTag}
                  <button onClick={() => setSelectedTag("All")} className="text-gray-400 hover:text-red-500 font-bold">✕</button>
                </span>
              )}

              {priceRange[1] < maxPrice && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-700 shadow-sm">
                  Price: Up to Rs. {priceRange[1].toLocaleString()}
                  <button onClick={() => setPriceRange([0, maxPrice])} className="text-gray-400 hover:text-red-500 font-bold">✕</button>
                </span>
              )}

              {showDiscountedOnly && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-700 shadow-sm">
                  On Sale
                  <button onClick={() => setShowDiscountedOnly(false)} className="text-gray-400 hover:text-red-500 font-bold">✕</button>
                </span>
              )}



              {searchQuery && (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-gray-200 rounded-full text-xs font-semibold text-gray-700 shadow-sm">
                  Keyword: "{searchQuery}"
                  <button onClick={() => setSearchQuery("")} className="text-gray-400 hover:text-red-500 font-bold">✕</button>
                </span>
              )}

              <button 
                onClick={clearFilters}
                className="text-xs font-extrabold text-red-600 hover:text-red-800 ml-auto mr-1 hover:underline"
              >
                Reset Filters
              </button>
            </div>
          )}

          {/* Product Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {currentProducts.map((product) => {
              const isOutOfStock = typeof product.inventory !== "undefined" && product.inventory <= 0;
              return (
                <div
                  key={product._id}
                  className="bg-white border border-gray-100 rounded-[24px] shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col p-3.5"
                >
                  {/* Image container */}
                  <Link 
                    href={`/${product.slug}`} 
                    className="relative overflow-hidden block aspect-square bg-[#F4F4F4] rounded-[20px] p-4 group-hover:opacity-95 transition-opacity"
                  >
                    <Image
                      src={product.imageUrl}
                      alt={product.title}
                      fill
                      className="object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                      sizes="(max-width: 768px) 50vw, (max-width: 1200px) 33vw, 25vw"
                    />

                    {isOutOfStock && (
                      <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center rounded-[20px]">
                        <span className="bg-red-600 text-white font-black text-[10px] sm:text-xs tracking-widest uppercase px-3 py-1.5 rounded-lg shadow-md">
                          Out of Stock
                        </span>
                      </div>
                    )}
                    
                    {product.discountPercentage > 0 && !isOutOfStock && (
                      <span className="absolute top-3 left-3 text-[#B34493] font-bold text-[9px] sm:text-[10px] tracking-wider uppercase bg-white/80 backdrop-blur-sm px-2 py-0.5 rounded-md">
                        WAS Rs. {Math.round(product.price / (1 - product.discountPercentage / 100)).toLocaleString()}
                      </span>
                    )}
                  </Link>

                  {/* Info Container */}
                  <div className="pt-4 px-1 flex flex-col flex-1">
                    {/* Title & Discount percentage */}
                    <div className="flex justify-between items-start gap-2">
                      <Link href={`/${product.slug}`} className="min-w-0 flex-1">
                        <h3 className="font-bold text-gray-900 text-sm sm:text-base truncate hover:text-[#B88E2F] transition-colors">
                          {product.title}
                        </h3>
                      </Link>
                      {product.discountPercentage > 0 && (
                        <span className="text-[#E23F51] font-bold text-xs sm:text-sm flex-shrink-0">
                          {product.discountPercentage}% OFF
                        </span>
                      )}
                    </div>

                    {/* Description */}
                    <p className="text-[11px] sm:text-xs text-gray-400 line-clamp-1 mt-0.5">
                      {product.description || "Premium School & Office Supply"}
                    </p>

                    {/* Pricing & Buy Button */}
                    <div className="flex items-center justify-between mt-auto pt-2.5 border-t border-gray-50">
                      <div className="flex flex-col">
                        {product.discountPercentage > 0 && (
                          <span className="text-[10px] sm:text-xs text-gray-400 line-through leading-none mb-0.5">
                            Rs. {Math.round(product.price / (1 - product.discountPercentage / 100)).toLocaleString()}
                          </span>
                        )}
                        <span className="text-sm sm:text-base font-extrabold text-gray-900 leading-none">
                          Rs. {product.price.toLocaleString()}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-2">
                        {/* Add to Cart Icon Button */}
                        <button
                          onClick={() => handleAddToCart(product)}
                          disabled={addingItems.has(product._id) || isOutOfStock}
                          className={`w-9 h-9 rounded-full flex items-center justify-center border transition-all duration-300 ${
                            isOutOfStock
                              ? "bg-gray-100 border-gray-100 text-gray-300 cursor-not-allowed"
                              : addedItems.has(product._id)
                              ? "bg-green-600 border-green-600 text-white shadow-sm"
                              : addingItems.has(product._id)
                              ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed"
                              : "border-gray-200 hover:border-black text-gray-700 hover:text-black bg-white hover:shadow-sm"
                          }`}
                          title={isOutOfStock ? "Out of Stock" : "Add to Cart"}
                        >
                          {addingItems.has(product._id) ? (
                            <svg className="animate-spin h-3.5 w-3.5" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                          ) : addedItems.has(product._id) ? (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                            </svg>
                          )}
                        </button>

                        {/* Buy Now Button */}
                        {isOutOfStock ? (
                          <button
                            disabled
                            className="rounded-full bg-gray-100 text-gray-400 border border-gray-200 font-bold text-[10px] sm:text-xs px-3 py-2 cursor-not-allowed whitespace-nowrap"
                          >
                            Sold Out
                          </button>
                        ) : (
                          <Link
                            href={`/${product.slug}`}
                            className="rounded-full bg-black text-white font-bold text-[10px] sm:text-xs px-3 py-2 hover:bg-gray-800 transition text-center whitespace-nowrap shadow-sm hover:shadow"
                          >
                            Buy Now
                          </Link>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Empty State */}
          {currentProducts.length === 0 && (
            <div className="text-center py-16">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <h3 className="text-lg font-semibold text-gray-700 mb-1">No products found</h3>
              <p className="text-sm text-gray-500 mb-4">Try adjusting your filters or search criteria.</p>
              <button onClick={clearFilters} className="px-5 py-2 bg-[#B88E2F] text-white rounded-lg text-sm font-medium hover:bg-[#8C6D23] transition-colors">
                Reset Filters
              </button>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-12">
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-[#B88E2F] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-xs sm:text-sm font-bold"
              >
                Previous
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, idx, arr) => (
                  <React.Fragment key={page}>
                    {idx > 0 && arr[idx - 1] !== page - 1 && (
                      <span className="px-2 text-gray-400 text-xs sm:text-sm font-bold">…</span>
                    )}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`w-9 h-9 flex items-center justify-center rounded-xl text-xs sm:text-sm font-bold transition-colors ${
                        currentPage === page
                          ? "bg-[#B88E2F] text-white shadow-md"
                          : "bg-gray-100 text-gray-700 hover:bg-[#B88E2F] hover:text-white"
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}

              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-[#B88E2F] hover:text-white transition-colors disabled:opacity-40 disabled:cursor-not-allowed text-xs sm:text-sm font-bold"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ─── Mobile Filters Slide-over / Modal ─── */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-[100] flex justify-end">
          {/* Backdrop */}
          <div 
            onClick={() => setMobileFiltersOpen(false)}
            className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" 
          />
          {/* Panel */}
          <div className="relative w-80 max-w-full bg-white h-full flex flex-col p-6 shadow-2xl animate-in slide-in-from-right duration-350 overflow-y-auto">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-6">
              <h2 className="text-base font-extrabold text-gray-900 uppercase tracking-wider">Filters</h2>
              <button 
                onClick={() => setMobileFiltersOpen(false)}
                className="text-gray-400 hover:text-gray-600 font-bold"
              >
                ✕
              </button>
            </div>

            {/* Categories */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Categories</h3>
              <div className="space-y-1">
                <button
                  onClick={() => { setSelectedCategory("All"); setMobileFiltersOpen(false); }}
                  className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                    selectedCategory === "All" ? "bg-[#F9F1E7] text-[#B88E2F]" : "text-gray-600"
                  }`}
                >
                  <span>All Categories</span>
                  <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                    {products.length}
                  </span>
                </button>
                {categories.filter(c => !c.parentId).map((parent) => {
                  const parentCount = categoryCounts[parent.name] || 0;
                  const subCats = categories.filter(sub => sub.parentId === parent._id);
                  return (
                    <div key={parent._id} className="space-y-0.5">
                      <button
                        onClick={() => { setSelectedCategory(parent.name); setMobileFiltersOpen(false); }}
                        className={`w-full flex items-center justify-between text-left px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                          selectedCategory === parent.name ? "bg-[#F9F1E7] text-[#B88E2F]" : "text-gray-700"
                        }`}
                      >
                        <span className="truncate">{parent.name}</span>
                        {parentCount > 0 && (
                          <span className="text-[9px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full font-bold">
                            {parentCount}
                          </span>
                        )}
                      </button>
                      {subCats.map((sub) => {
                        const subCount = categoryCounts[sub.name] || 0;
                        return (
                          <button
                            key={sub._id}
                            onClick={() => { setSelectedCategory(sub.name); setMobileFiltersOpen(false); }}
                            className={`w-full flex items-center justify-between text-left pl-6 pr-3 py-1.5 rounded-xl text-[12px] font-medium transition-all ${
                              selectedCategory === sub.name ? "bg-[#F9F1E7]/60 text-[#B88E2F]" : "text-gray-500"
                            }`}
                          >
                            <span className="truncate">{sub.name}</span>
                            {subCount > 0 && (
                              <span className="text-[8px] bg-gray-100 text-gray-400 px-1.5 py-0.2 rounded-full">
                                {subCount}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Promotions */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Promotions</h3>
              <div className="space-y-2">
                <label className="flex items-center gap-3 cursor-pointer py-0.5">
                  <input
                    type="checkbox"
                    checked={showDiscountedOnly}
                    onChange={(e) => setShowDiscountedOnly(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-[#B88E2F]"
                  />
                  <span className="text-xs sm:text-sm font-semibold text-gray-700">Discounted Only</span>
                </label>
              </div>
            </div>

            {/* Price Filter */}
            <div className="space-y-3 mb-6">
              <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Price</h3>
              <input
                type="range"
                min={0}
                max={maxPrice}
                step={50}
                value={priceRange[1]}
                onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])}
                className="w-full accent-[#B88E2F]"
              />
              <div className="text-xs font-bold text-gray-800">
                Up to Rs. {priceRange[1].toLocaleString()}
              </div>
            </div>

            {/* Mobile Tags Filter */}
            {allTags.length > 0 && (
              <div className="space-y-3 mb-8">
                <h3 className="text-sm font-bold text-gray-800 uppercase tracking-wide">Filter by Tags</h3>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => { setSelectedTag("All"); setMobileFiltersOpen(false); }}
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${
                      selectedTag === "All"
                        ? "bg-[#B88E2F] border-[#B88E2F] text-white"
                        : "bg-white border-gray-200 text-gray-600"
                    }`}
                  >
                    All Tags
                  </button>
                  {allTags.map((tag) => (
                    <button
                      key={tag}
                      onClick={() => { setSelectedTag(tag); setMobileFiltersOpen(false); }}
                      className={`px-3 py-1.5 rounded-xl text-xs font-bold border ${
                        selectedTag === tag
                          ? "bg-[#B88E2F] border-[#B88E2F] text-white"
                          : "bg-white border-gray-200 text-gray-600"
                      }`}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="mt-auto grid grid-cols-2 gap-3 pt-4 border-t border-gray-100">
              <button 
                onClick={() => { clearFilters(); setMobileFiltersOpen(false); }}
                className="py-2.5 rounded-xl border border-gray-200 text-xs font-bold text-gray-600 text-center"
              >
                Clear All
              </button>
              <button 
                onClick={() => setMobileFiltersOpen(false)}
                className="py-2.5 rounded-xl bg-black text-white text-xs font-bold text-center"
              >
                Show Results
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductCards;
